import {
  FlagOverridePlugin,
  EventInterceptionPlugin,
} from '@launchdarkly/toolbar/plugins'
import { addFeatureFlagEvaluation } from '@openphone/quo-observability'
import * as LDClient from 'launchdarkly-js-client-sdk'
import isEqual from 'lodash/fp/isEqual'
import { action, makeObservable, observable, reaction, runInAction } from 'mobx'

import config from '@src/config'
import type Service from '@src/service'
import type { OrganizationModel, SubscriptionModel, UserModel } from '@src/service/model'
import makePersistable from '@src/service/storage/makePersistable'
import type { KeyValueRepository } from '@src/service/worker/repository'

import type { FlagKey, Flags, FlagValue } from '@odyssey/feature-flags/FlagsTypes'
import { defaultFlags } from '@odyssey/feature-flags/FlagsTypes'

export const flagOverridePlugin = new FlagOverridePlugin()
export const eventInterceptionPlugin = new EventInterceptionPlugin()

const staticAnonymousUser: LDClient.LDSingleKindContext = {
  kind: 'anonUser',
  anonymous: true,
}

type Custom = NonNullable<
  Record<string, string | boolean | number | (string | boolean | number)[]>
>

export interface IFlagsService {
  flags: Flags
  readonly overrides: {
    setOverride: FlagOverridePlugin['setOverride']
    removeOverride: FlagOverridePlugin['removeOverride']
    getAllOverrides: FlagOverridePlugin['getAllOverrides']
  }
  readonly initialized: boolean
  identify(
    user: UserModel,
    organization: OrganizationModel,
    subscription: SubscriptionModel,
    workspaceSize: number,
    desktopVersion?: string,
  ): Promise<void>
  /**
   * Creates a subscription to flag changes. This function is meant to be used from
   * the Odyssey codebase useFlag hook.
   *
   * @returns A function to unsubscribe from the flag change subscription.
   */
  subscribeToFlag<T extends FlagKey>(
    flag: T,
    onChangeCallback: (value: FlagValue<T>) => void,
  ): () => void
  waitUntilReady(): Promise<void>
  tearDown(): Promise<void>
}

export default class FlagsService implements IFlagsService {
  readonly overrides = flagOverridePlugin
  private ldClient: LDClient.LDClient
  initialized = false

  flags: Flags = defaultFlags

  constructor(private readonly root: Service) {
    makeObservable<this, 'syncFlags' | 'handleChange'>(this, {
      flags: observable,
      syncFlags: action.bound,
      handleChange: action.bound,
      init: action.bound,
      initialized: observable,
      subscribeToFlag: action.bound,
    })

    makePersistable(this, 'FlagsService', {
      flags: this.root.storage.async(),
    })

    this.ldClient = LDClient.initialize(
      config.LAUNCH_DARKLY_CLIENT_SIDE_ID,
      {
        kind: 'multi',
        user: staticAnonymousUser,
      },
      {
        plugins: [flagOverridePlugin, eventInterceptionPlugin],
        inspectors: [
          {
            type: 'flag-detail-changed',
            name: 'Flag Detail Changed',
            method: (key, changeDetail) => {
              addFeatureFlagEvaluation(key, changeDetail.value)
            },
          },
        ],
      },
    )

    this.ldClient.on('change', this.handleChange.bind(this))
  }

  async init() {
    const table = this.root.storage.table<KeyValueRepository>('keyValue')
    const values = await table.all()
    const keys = await table.keys()

    const flagsKey = keys.findIndex((key) => key === 'FlagsService.flags')
    if (flagsKey < 0) {
      return
    }

    const flags = values[flagsKey]

    if (flags) {
      runInAction(() => {
        for (const [key, value] of Object.entries(flags)) {
          if (this.flags[key] !== undefined && !isEqual(this.flags[key], value)) {
            this.flags[key] = value
          }
        }
      })
    }
  }

  async tearDown() {
    await this.ldClient.close()
  }

  async waitUntilReady() {
    await this.init()
    await this.ldClient.waitUntilReady()
    this.syncFlags()
    this.initialized = true
  }

  async identify(
    user: UserModel,
    organization: OrganizationModel,
    subscription: SubscriptionModel,
    workspaceSize: number,
    desktopVersion?: string,
  ) {
    const custom: Custom = {
      appVersion: config.VERSION,
      organizationId: organization.id,
      organizationCreatedAt: new Date(organization.createdAt ?? 0).toISOString(),
      desktopVersion: desktopVersion ?? '',
    }

    if (subscription.type) {
      custom.subscriptionType = subscription.type
    }

    await this.ldClient.identify({
      kind: 'multi',
      user: {
        key: user.id,
        anonymous: false,
        avatar: user.pictureUrl ?? '',
        email: user.email ?? '',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        createdAt: user.createdAt ?? '',
        ...custom,
      },
      anonUser: staticAnonymousUser,
      organization: {
        key: organization.id,
        subscriptionCreatedAt: subscription.createdAt,
        subscriptionType: subscription.type,
        memberCount: workspaceSize,
      },
    })
  }

  subscribeToFlag<T extends FlagKey>(
    flag: T,
    onChangeCallback: (value: FlagValue<T>) => void,
  ) {
    // Reaction to subscribe to individual flag changes and call the onChangeCallback
    // with the new value.
    const dispose = reaction(
      () => this.flags[flag],
      (current) => {
        onChangeCallback(current)
      },
    )

    return () => {
      dispose()
    }
  }

  private handleChange(updatedFlags: LDClient.LDFlagChangeset) {
    // Environment change events do not include local overrides.
    const effectiveFlags = this.ldClient.allFlags()

    for (const key of Object.keys(updatedFlags)) {
      if (
        this.flags[key] !== undefined &&
        !isEqual(this.flags[key], effectiveFlags[key])
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.flags[key] = effectiveFlags[key]
      }
    }
  }

  private syncFlags() {
    const newFlags = this.ldClient.allFlags()

    for (const [key, value] of Object.entries(this.flags)) {
      if (newFlags[key] !== undefined && !isEqual(newFlags[key], value)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.flags[key] = newFlags[key]
      }
    }
  }
}
